import React from 'react';
import { X, ShoppingCart, Plus, Minus, Package, ArrowRight } from 'lucide-react';
import { useCart } from './PreOrderCartContext';

const formatCurrency = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const PreOrderCart = ({ onCheckout }) => {
  const { cartItems, updateQuantity, removeFromCart, getTotalPrice, isCartOpen, setIsCartOpen } = useCart();
  
  const handleCheckout = () => {
    setIsCartOpen(false); // Close cart
    setTimeout(() => {
      onCheckout(); // Open checkout after a small delay for smooth transition
    }, 300);
  };
  
  if (!isCartOpen) return null;

  return (
    // Higher z-index to ensure it's above everything including StickyNav
    <div className="fixed inset-0 z-[60]">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
        onClick={() => setIsCartOpen(false)} 
      />
      
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[70] 
        transform transition-transform duration-300 ease-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-primary/10 to-cta/10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <ShoppingCart size={24} />
                Pre-Order Cart
              </h2>
              <button 
                onClick={() => setIsCartOpen(false)} 
                className="hover:bg-gray-100 p-2 rounded-full transition-colors"
                aria-label="Close cart"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Pre-order notice */}
          {cartItems.length > 0 && (
            <div className="bg-orange-50 p-3 border-b flex items-start gap-2">
              <Package className="text-orange-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-orange-800">
                <span className="font-bold">Pre-Order Items</span>
                <br />
                4-6 weeks production • Pickup at events only
              </div>
            </div>
          )}

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500 mb-4">Your cart is empty</p>
                <button 
                  onClick={() => setIsCartOpen(false)}
                  className="text-primary font-medium hover:underline"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item, i) => (
                  <div key={`${item.id}-${item.size}-${i}`} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">{item.name}</h3>
                        <p className="text-sm text-gray-600">
                          {item.color} • Size {item.size}
                        </p>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.id, item.size)} 
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove item"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updateQuantity(item.id, item.size, Math.max(0, item.quantity - 1))}
                          className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-100 
                            transition-colors flex items-center justify-center"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="w-12 text-center font-medium" aria-live="polite">
                          {item.quantity}
                        </span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.size, item.quantity + 1)}
                          className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-100 
                            transition-colors flex items-center justify-center"
                          aria-label="Increase quantity"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <span className="font-bold text-gray-800">
                        {formatCurrency(item.price * item.quantity)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with total and checkout */}
          {cartItems.length > 0 && (
            <div className="border-t p-4 bg-gray-50">
              <div className="flex justify-between mb-4">
                <span className="text-lg">Subtotal:</span>
                <span className="text-lg font-bold">{formatCurrency(getTotalPrice())}</span>
              </div>
              
              <button 
                onClick={handleCheckout}
                className="w-full bg-gradient-to-r from-primary to-cta text-white py-3 rounded-lg 
                  font-bold hover:shadow-lg transform hover:scale-[1.02] transition-all 
                  flex items-center justify-center gap-2 group"
              >
                Proceed to Checkout
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              
              <p className="text-xs text-center text-gray-500 mt-3">
                Secure checkout powered by Stripe
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreOrderCart;